import React, {
  Fragment,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import Animation from '@adsk/alloy-react-animation';
import { useMeasure, getValidChildren } from '@adsk/alloy-react-helpers';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import TabTitle from './TabTitle';

type ChildTabType = {
  props: {
    tab: string;
    onClick: () => void;
  };
};

const isActive = (selected: string, child: ChildTabType) =>
  child.props.tab === selected;

const firstTab = (children: ReactNode) => {
  const tabs = React.Children.toArray(children) as ChildTabType[];

  if (!tabs.length) {
    return '';
  }

  return tabs[0].props.tab;
};

const LABEL_WIDTH = {
  AUTO: 'auto',
  FULL: 'full',
};

type LabelWidths = typeof LABEL_WIDTH;
export type LabelWidth = LabelWidths[keyof LabelWidths];

type TabsProps = Omit<StylableComponent<HTMLDivElement>, 'onChange'> & {
  active?: string;
  initialTab?: string;
  canClickSelectedTab?: boolean;
  labelWidth?: LabelWidth;
  onChange?: (tab: string) => void;
  renderExtraItems?: () => ReactElement;
};

/**
 * Tabs keep related content in a single container that is shown and hidden through navigation.
 */
const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      style,
      className,
      children,
      active,
      initialTab,
      canClickSelectedTab = true,
      onChange,
      labelWidth = LABEL_WIDTH.AUTO,
      renderExtraItems,
      ...props
    },
    ref,
  ) => {
    const [activeTab, setActiveTab] = useState(
      initialTab || firstTab(children),
    );
    const [focusedTab, setFocusedTab] = useState<string | null>(null);

    const childRefs = useRef<{ [tab: string]: HTMLButtonElement }>({});
    const tabListRef = useRef<HTMLDivElement>(null);
    const bounds = useMeasure(tabListRef);
    const [slider, setSlider] = useState<{
      hasValue?: boolean;
      left: number;
      width: number;
    }>({ left: 0, width: 0 });
    const finalActiveTab = active || activeTab;

    useEffect(() => {
      const target = childRefs.current[finalActiveTab];
      const container = tabListRef.current;
      if (target && container) {
        const cRect = container.getBoundingClientRect();
        if (cRect.width === 0) {
          return;
        }

        const tRect = target.getBoundingClientRect();
        const left = tRect.left - cRect.left;

        setSlider({
          hasValue: true,
          left,
          width: tRect.width,
        });
      }
    }, [finalActiveTab, bounds]);

    const handleTabSelection = (child: ChildTabType) => {
      const { tab, onClick } = child.props;

      !active && setActiveTab(tab);
      onChange && onChange(tab);
      onClick && onClick();
    };

    const blurFocusedTab = () => {
      if (!focusedTab) {
        return;
      }

      const focusedTabElement = childRefs.current[focusedTab];
      focusedTabElement && focusedTabElement.blur();
    };

    const handleOnClick = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      child: ReactElement,
    ) => {
      // ctrl+click or cmd+click should have default behavior of anchor link
      if (e.ctrlKey || e.metaKey) {
        blurFocusedTab();
        return;
      }
      e.preventDefault();
      (canClickSelectedTab || finalActiveTab !== child.props.tab) &&
        handleTabSelection(child);
      blurFocusedTab();
    };

    const handleOnFocus = (child: ChildTabType) =>
      setFocusedTab(child.props.tab);
    const handleOnBlur = () => setFocusedTab(null);
    const tabTitleStyle =
      labelWidth === LABEL_WIDTH.FULL ? { flex: 1 } : undefined;

    return (
      <div
        {...props}
        className={className}
        ref={ref}
        css={[
          {
            display: 'block',
          },
          style,
        ]}
      >
        <div
          ref={tabListRef}
          css={[
            {
              position: 'relative',
              display: 'flex',
              borderBottomWidth: '1px',
              borderBottomStyle: 'solid',
              borderBottomColor: theme.colors.charcoal200,
            },
          ]}
        >
          <div
            role="tablist"
            css={[
              {
                display: 'flex',
                flex: 1,
              },
            ]}
          >
            {React.Children.map(getValidChildren(children), (child, key) => (
              <TabTitle
                key={key}
                disabled={
                  (isActive(finalActiveTab, child) && !canClickSelectedTab) ||
                  child.props.disabled
                }
                active={isActive(finalActiveTab, child)}
                onClick={(e) => handleOnClick(e, child)}
                onBlur={() => handleOnBlur()}
                onFocus={() => handleOnFocus(child)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Space') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleTabSelection(child);
                  }
                }}
                ref={(el) => {
                  if (el) {
                    childRefs.current[child.props.tab] = el;
                  }
                }}
                renderIcon={child.props.renderIcon}
                {...child.props.labelProps}
                style={[tabTitleStyle, child.props?.labelProps?.style]}
              >
                {child.props.label}
              </TabTitle>
            ))}
          </div>
          {renderExtraItems?.()}
          {slider.hasValue && (
            <Animation
              style={[
                {
                  height: 2,
                  bottom: -1,
                  position: 'absolute',
                  background: theme.colors.adskBlue500,
                },
                {
                  left: slider.left,
                  width: slider.width,
                },
              ]}
              layout
              initial={false}
            />
          )}
        </div>

        <Fragment>
          {React.Children.map(
            getValidChildren(children),
            (child) => isActive(finalActiveTab, child) && child,
          )}
        </Fragment>
      </div>
    );
  },
);

Tabs.displayName = 'Tabs';

Tabs.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The component's children tabs */
  children: PropTypes.node,
  /** selected tab on init of tabs */
  initialTab: PropTypes.string,
  /** controlled selected tab */
  active: PropTypes.string,
  /** onChange callback which returns key */
  onChange: PropTypes.func,
  /** Whether selected tabs are clickable or not. Defaults to true */
  canClickSelectedTab: PropTypes.bool,
  /** Whether the tab label width should be based on text or take full width. */
  labelWidth: PropTypes.oneOf(Object.values(LABEL_WIDTH)),
  /** Extra items render function */
  renderExtraItems: PropTypes.func,
};

export default Object.assign(Tabs, { LABEL_WIDTH });
